import { Component, OnInit, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/compat/storage';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';

import { ProjectLanguage } from '../firestore-models/project-language';
import { Facility } from '../firestore-models/facility';
import { Floor } from '../firestore-models/floor';
import { Exhibit } from '../firestore-models/exhibit';

import { ConfigurationService } from '../configuration.service';
import { AppConst } from '../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-voice-file-download',
  templateUrl: './voice-file-download.component.html',
  styleUrls: ['./voice-file-download.component.css'],
  standalone: false,
})
export class VoiceFileDownloadComponent implements OnInit {

  // FireStoreコレクション
  private facilitiesCollection!: AngularFirestoreCollection<Facility>;
  private floorsCollection!: AngularFirestoreCollection<Floor>;
  private exhibitsCollection!: AngularFirestoreCollection<Exhibit>;

  private facilitiesObservable!: Observable<Facility[]>;
  private facilitiesSubscribe!: Subscription;
  private floorsObservable!: Observable<Floor[]>;
  private floorsSubscribe!: Subscription;
  private exhibitsObservable!: Observable<Exhibit[]>;
  private exhibitsSubscribe!: Subscription;

  //プロジェクトID
  projectId: string;

  //音声ファイルダウンロード情報テーブル
  displayedColumns: string[] = ['position', 'category', 'serial_id', 'id', 'name', 'language', 'download'];
  dataSource = new MatTableDataSource<any>();
  selection = new SelectionModel<any>(true, []);
  element!: HTMLElement;

  reservationFacilityCollectionName = AppConst.FIRESTORE_RESERVATION_FACILITIES;

  //プロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private angularFireStorage: AngularFireStorage,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 施設取得
    this.facilitiesCollection = this.angularFirestore.collection<Facility>(
      AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('facility_id', 'asc'));
    this.facilitiesObservable = this.facilitiesCollection.valueChanges({ idField: 'id' });
    this.facilitiesSubscribe = this.facilitiesObservable.subscribe(facilities => {
      for (const facility of facilities) {
        if (facility.facility_descs.desc_details && facility.facility_descs.desc_details.length > 0) {
          for(const desc_detail of facility.facility_descs.desc_details) {
            let languageText = desc_detail.lang_code;
            for (const projectLanguage of this.projectLanguages) {
              if (projectLanguage.lang_code == desc_detail.lang_code) {
                this.projectBaseLang == 'ja' ? languageText = projectLanguage.lang_name : languageText = projectLanguage.lang_name_en;
                break;
              }
            }
            this.dataSource.data.push({'category': this.translate.instant('施設'), 'serial_id': 'facility_'+facility.facility_id, 'id': facility.facility_id, 'name': facility.facility_names, 'language': languageText, 'download':desc_detail.voice_file_info, 'filterValue': facility.facility_names[0].name});
          }
        }
      }
      this.facilitiesSubscribe.unsubscribe();
      // フロア・表示グループ取得
      this.floorsCollection = this.angularFirestore.collection<Floor>(
        AppConst.FIRESTORE_FLOORS, ref => ref.where('project_id', '==', this.projectId).orderBy('floor_id', 'asc'));
      this.floorsObservable = this.floorsCollection.valueChanges({ idField: 'id' });
      this.floorsSubscribe = this.floorsObservable.subscribe(floors => {
        for (const floor of floors) {
          if (floor.floor_descs.desc_details && floor.floor_descs.desc_details.length > 0) {
            for(const desc_detail of floor.floor_descs.desc_details) {
              let languageText = desc_detail.lang_code;
              for (const projectLanguage of this.projectLanguages) {
                if (projectLanguage.lang_code == desc_detail.lang_code) {
                  this.projectBaseLang == 'ja' ? languageText = projectLanguage.lang_name : languageText = projectLanguage.lang_name_en;
                  break;
                }
              }
              this.dataSource.data.push({'category': this.translate.instant('フロア・表示グループ'), 'serial_id': 'floor_'+floor.floor_id, 'id': floor.floor_id, 'name': floor.floor_names, 'language': languageText, 'download':desc_detail.voice_file_info, 'filterValue': floor.floor_names[0].name});
            }
          }
        }
        this.floorsSubscribe.unsubscribe();
        // 展示取得
        this.exhibitsCollection = this.angularFirestore.collection<Exhibit>(
          AppConst.FIRESTORE_EXHIBITS, ref => ref.where('project_id', '==', this.projectId).orderBy('exhibit_id', 'asc'));
        this.exhibitsObservable = this.exhibitsCollection.valueChanges({ idField: 'id' });
        this.exhibitsSubscribe = this.exhibitsObservable.subscribe(exhibits => {
          for (const exhibit of exhibits) {
            if (exhibit.exhibit_descs.desc_details && exhibit.exhibit_descs.desc_details.length > 0) {
              for(const desc_detail of exhibit.exhibit_descs.desc_details) {
                let languageText = desc_detail.lang_code;
                for (const projectLanguage of this.projectLanguages) {
                  if (projectLanguage.lang_code == desc_detail.lang_code) {
                    this.projectBaseLang == 'ja' ? languageText = projectLanguage.lang_name : languageText = projectLanguage.lang_name_en;
                    break;
                  }
                }
                this.dataSource.data.push({'category': this.translate.instant('展示'), 'serial_id': 'exhibit_'+exhibit.exhibit_id, 'id': exhibit.exhibit_id, 'name': exhibit.exhibit_names, 'language': languageText, 'download':desc_detail.voice_file_info, 'filterValue': exhibit.exhibit_names[0].name});
              }
            }
          }
          this.exhibitsSubscribe.unsubscribe();
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        });
      });
    });    
  }

  ngOnDestroy(): void {
    this.facilitiesSubscribe.unsubscribe();
    this.floorsSubscribe.unsubscribe();
    this.exhibitsSubscribe.unsubscribe();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  
    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'serial_id':
          return compare(a.id, b.id, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  download(files: any): void {
    if (files && files.download) {
      const path = `${AppConst.FILE_UPLOAD_ROOT_DIR_NAME}/${this.projectId}/${AppConst.VOICE_FILE_UPLOAD_DIR_NAME}/${files.download.file_name}`;
      const storageRef = this.angularFireStorage.ref(path);
      storageRef.getDownloadURL().subscribe(async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `${files.serial_id}_${files.language}.mp3`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });
    }
  }

}
